import { collection, doc, getDocs, addDoc, updateDoc, serverTimestamp, query, orderBy, where, getDoc, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';
import { getAuth } from 'firebase/auth';
import type { FinanceRecord, FinanceFormData, CurrencyTotals, SearchableOfferingType, FinanceEvidenceFile } from '../types/newFinance';

// 🌟 새로운 간단한 재정관리 서비스
const financeRecordsRef = collection(db, 'finance-records');

/**
 * 통화별 합계 계산 헬퍼 함수
 */
export const calculateCurrencyTotals = (items: { amount: number; currency: string }[]): CurrencyTotals => {
  return items.reduce((totals, item) => {
    totals[item.currency as keyof CurrencyTotals] += item.amount;
    return totals;
  }, { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 });
};

/**
 * 순손익 계산 헬퍼 함수
 */
export const calculateNetTotals = (incomeTotals: CurrencyTotals, expenseTotals: CurrencyTotals): CurrencyTotals => {
  return {
    HUF: incomeTotals.HUF - expenseTotals.HUF,
    EUR: incomeTotals.EUR - expenseTotals.EUR,
    USD: incomeTotals.USD - expenseTotals.USD,
    KRW: incomeTotals.KRW - expenseTotals.KRW,
    GBP: (incomeTotals.GBP || 0) - (expenseTotals.GBP || 0),
    CAD: (incomeTotals.CAD || 0) - (expenseTotals.CAD || 0)
  };
};

/**
 * 통화별 합계 표시 문자열 생성
 */
export const formatCurrencyTotals = (totals: CurrencyTotals): string => {
  const nonZeroAmounts = (['HUF', 'EUR', 'USD', 'KRW', 'GBP', 'CAD'] as const)
    .filter((currency) => (totals[currency] || 0) !== 0)
    .map((currency) => `${(totals[currency] || 0).toLocaleString()} ${currency}`);

  return nonZeroAmounts.length > 0 ? nonZeroAmounts.join(' + ') : '0 HUF';
};

/**
 * 재정 데이터에서 참여자 이름들 추출
 */
export const extractParticipantNames = (formData: FinanceFormData): string[] => {
  const names = new Set<string>();
  const addNames = (items: { name?: string }[]) => items.forEach(item => {
    const name = item.name?.trim();
    if (name) names.add(name);
  });
  addNames(formData.titheItems);
  addNames(formData.thanksgivingItems);
  addNames(formData.otherIncomeItems);
  return Array.from(names);
};

/**
 * 재정 데이터에서 헌금 종류들 추출
 */
export const extractOfferingTypes = (formData: FinanceFormData): string[] => {
  const types = new Set<string>();
  if (formData.titheItems.some(item => item.amount > 0)) types.add('tithe');
  if (formData.thanksgivingItems.some(item => item.amount > 0)) types.add('thanksgiving');
  formData.otherIncomeItems.forEach(item => item.amount > 0 && types.add(item.category));
  return Array.from(types);
};

/**
 * 새로운 재정관리 서비스
 */
export const newFinanceService = {
  /**
   * 재정 기록 생성
   */
  async createFinanceRecord(formData: FinanceFormData): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("사용자 인증 정보가 없습니다.");

    // ID 생성을 위한 모든 아이템 수집
    const allIncomeItems = [
      ...formData.titheItems,
      ...formData.thanksgivingItems,
      ...formData.otherIncomeItems
    ];
    const allExpenseItems = formData.expenseItems;

    // ID 추가
    const titheItems = formData.titheItems.map((item, index) => ({
      ...item,
      id: `tithe-${index}-${Date.now()}`
    }));

    const thanksgivingItems = formData.thanksgivingItems.map((item, index) => ({
      ...item,
      id: `thanksgiving-${index}-${Date.now()}`
    }));

    const otherIncomeItems = formData.otherIncomeItems.map((item, index) => ({
      ...item,
      id: `other-${index}-${Date.now()}`
    }));

    const expenseItems = formData.expenseItems.map((item, index) => ({
      ...item,
      id: `expense-${index}-${Date.now()}`
    }));

    // 합계 계산
    const incomeTotals = calculateCurrencyTotals([
      ...titheItems,
      ...thanksgivingItems,
      ...otherIncomeItems
    ]);

    const expenseTotals = calculateCurrencyTotals(expenseItems);
    const personalAdvanceTotals = calculateCurrencyTotals(expenseItems.filter(item => item.paymentSource === 'personal-advance' && !item.reimbursed));
    const netTotals = calculateNetTotals(incomeTotals, expenseTotals);

    // 검색용 필드들 생성
    const participantNames = extractParticipantNames(formData);
    const offeringTypes = extractOfferingTypes(formData);

    const record: Omit<FinanceRecord, 'id'> = {
      date: formData.date,
      titheItems,
      thanksgivingItems,
      otherIncomeItems,
      expenseItems,
      incomeTotals,
      expenseTotals,
      netTotals,
      personalAdvanceTotals,
      participantNames,
      offeringTypes,
      status: 'pending',
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: new Date()
    };

    const docRef = await addDoc(financeRecordsRef, {
      ...record,
      createdAt: serverTimestamp()
    });

    return docRef.id;
  },

  /**
   * 모든 재정 기록 조회
   */
  async getAllFinanceRecords(): Promise<FinanceRecord[]> {
    const q = query(
      financeRecordsRef,
      where('status', '!=', 'superseded'),
      orderBy('status'),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        approvedAt: data.approvedAt?.toDate(),
        modifiedAt: data.modifiedAt?.toDate()
      } as FinanceRecord;
    });
  },

  /**
   * 상태별 재정 기록 조회
   */
  async getFinanceRecordsByStatus(status: 'pending' | 'approved' | 'reapproval' | 'superseded'): Promise<FinanceRecord[]> {
    const q = query(
      financeRecordsRef,
      where('status', '==', status),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        approvedAt: data.approvedAt?.toDate(),
        modifiedAt: data.modifiedAt?.toDate()
      } as FinanceRecord;
    });
  },

  /**
   * 특정 재정 기록 조회
   */
  async getFinanceRecordById(id: string): Promise<FinanceRecord | null> {
    const docRef = doc(financeRecordsRef, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: data.createdAt?.toDate() || new Date(),
      approvedAt: data.approvedAt?.toDate(),
      modifiedAt: data.modifiedAt?.toDate()
    } as FinanceRecord;
  },

  /**
   * 재정 기록 승인
   */
  async approveFinanceRecord(id: string): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("사용자 인증 정보가 없습니다.");

    const docRef = doc(financeRecordsRef, id);
    await updateDoc(docRef, {
      status: 'approved',
      approvedBy: user.displayName || user.email,
      approvedAt: serverTimestamp()
    });
  },

  /**
   * 재정 기록 수정 (재승인 요청)
   */
  async updateFinanceRecord(id: string, formData: FinanceFormData, reason: string): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("사용자 인증 정보가 없습니다.");

    // 기존 기록을 원본으로 유지하고 새로운 기록 생성
    const newRecordId = await this.createFinanceRecord(formData);

    // 새 기록을 수정 상태로 업데이트
    const newDocRef = doc(financeRecordsRef, newRecordId);
    await updateDoc(newDocRef, {
      status: 'reapproval',
      originalId: id,
      modificationReason: reason,
      modifiedBy: user.displayName || user.email,
      modifiedAt: serverTimestamp()
    });

    // 원본 기록을 폐기 상태로 변경
    const originalDocRef = doc(financeRecordsRef, id);
    await updateDoc(originalDocRef, {
      status: 'superseded',
      supersededBy: newRecordId,
      supersededAt: serverTimestamp()
    });

    return newRecordId;
  },

  /**
   * 재정 기록 삭제
   */
  async deleteFinanceRecord(id: string): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("사용자 인증 정보가 없습니다.");
    const docRef = doc(financeRecordsRef, id);
    await updateDoc(docRef, {
      status: 'superseded',
      deletionReason: '사용자 요청으로 보존 처리됨',
      deletedAt: serverTimestamp(),
      deletedBy: user.displayName || user.email || 'Unknown',
      updatedAt: serverTimestamp(),
    });
  },

  async attachEvidence(id: string, evidence: FinanceEvidenceFile): Promise<void> {
    await updateDoc(doc(financeRecordsRef, id), {
      evidenceFiles: arrayUnion(evidence),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * 이름으로 재정 기록 검색
   */
  async searchFinanceRecordsByName(name: string): Promise<FinanceRecord[]> {
    const q = query(
      financeRecordsRef,
      where('participantNames', 'array-contains', name),
      where('status', '!=', 'superseded'),
      orderBy('status'),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        approvedAt: data.approvedAt?.toDate(),
        modifiedAt: data.modifiedAt?.toDate()
      } as FinanceRecord;
    });
  },

  /**
   * 헌금 종류로 재정 기록 검색
   */
  async searchFinanceRecordsByOfferingType(offeringType: SearchableOfferingType): Promise<FinanceRecord[]> {
    const q = query(
      financeRecordsRef,
      where('offeringTypes', 'array-contains', offeringType),
      where('status', '!=', 'superseded'),
      orderBy('status'),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        approvedAt: data.approvedAt?.toDate(),
        modifiedAt: data.modifiedAt?.toDate()
      } as FinanceRecord;
    });
  },

  /**
   * 복합 검색: 이름 + 헌금종류 + 상태 + 날짜범위
   */
  async searchFinanceRecords(filters: {
    name?: string;
    offeringType?: SearchableOfferingType;
    status?: 'pending' | 'approved' | 'reapproval' | 'all';
    startDate?: string;
    endDate?: string;
  }): Promise<FinanceRecord[]> {
    let q = query(financeRecordsRef);

    // 기본적으로 superseded 상태 제외
    q = query(q, where('status', '!=', 'superseded'));

    // 이름 검색
    if (filters.name) {
      q = query(q, where('participantNames', 'array-contains', filters.name));
    }

    // 헌금 종류 검색
    if (filters.offeringType && filters.offeringType !== 'all') {
      q = query(q, where('offeringTypes', 'array-contains', filters.offeringType));
    }

    // 상태 필터
    if (filters.status && filters.status !== 'all') {
      q = query(q, where('status', '==', filters.status));
    } else {
      q = query(q, orderBy('status'));
    }

    // 날짜 정렬
    q = query(q, orderBy('date', 'desc'));

    const querySnapshot = await getDocs(q);

    let results = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        approvedAt: data.approvedAt?.toDate(),
        modifiedAt: data.modifiedAt?.toDate()
      } as FinanceRecord;
    });

    // 날짜 범위 필터 (클라이언트 측에서 처리 - Firestore 제한 때문)
    if (filters.startDate || filters.endDate) {
      results = results.filter(record => {
        const recordDate = record.date;
        if (filters.startDate && recordDate < filters.startDate) return false;
        if (filters.endDate && recordDate > filters.endDate) return false;
        return true;
      });
    }

    return results;
  },

  /**
   * 개인별 헌금 통계 조회
   */
  async getPersonalOfferingStats(name: string, year?: number): Promise<{
    totalRecords: number;
    titheTotal: CurrencyTotals;
    thanksgivingTotal: CurrencyTotals;
    otherTotal: CurrencyTotals;
    grandTotal: CurrencyTotals;
    records: FinanceRecord[];
  }> {
    // 해당 이름이 포함된 모든 레코드 조회
    const records = await this.searchFinanceRecordsByName(name);

    // 연도 필터 적용
    const filteredRecords = year
      ? records.filter(record => new Date(record.date).getFullYear() === year)
      : records;

    // 통계 계산
    const titheTotal: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };
    const thanksgivingTotal: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };
    const otherTotal: CurrencyTotals = { HUF: 0, EUR: 0, USD: 0, KRW: 0, GBP: 0, CAD: 0 };

    filteredRecords.forEach(record => {
      // 십일조 합계
      record.titheItems.forEach(item => {
        if (item.name === name) {
          titheTotal[item.currency] += item.amount;
        }
      });

      // 감사헌금 합계
      record.thanksgivingItems.forEach(item => {
        if (item.name === name) {
          thanksgivingTotal[item.currency] += item.amount;
        }
      });

      // 기타헌금 합계
      record.otherIncomeItems.forEach(item => {
        if (item.name === name) {
          otherTotal[item.currency] += item.amount;
        }
      });
    });

    // 총합계 계산
    const grandTotal: CurrencyTotals = {
      HUF: titheTotal.HUF + thanksgivingTotal.HUF + otherTotal.HUF,
      EUR: titheTotal.EUR + thanksgivingTotal.EUR + otherTotal.EUR,
      USD: titheTotal.USD + thanksgivingTotal.USD + otherTotal.USD,
      KRW: titheTotal.KRW + thanksgivingTotal.KRW + otherTotal.KRW,
      GBP: titheTotal.GBP + thanksgivingTotal.GBP + otherTotal.GBP,
      CAD: titheTotal.CAD + thanksgivingTotal.CAD + otherTotal.CAD
    };

    return {
      totalRecords: filteredRecords.length,
      titheTotal,
      thanksgivingTotal,
      otherTotal,
      grandTotal,
      records: filteredRecords
    };
  }
};
